import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Motion";
import { useLanguage } from "@/i18n";

export default function Faq() {
  const { content } = useLanguage();
  const faqs = content.faqs;
  const [openIdx, setOpenIdx] = useState(0);

  if (!faqs) return null;

  return (
    <section data-testid="faq-section" className="border-t border-stone-300/70 bg-[#F0ECE4]">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:py-32 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <Eyebrow>{faqs.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-5xl">
                {faqs.titleA} <span className="italic text-[#8F6B3C]">{faqs.titleB}</span>
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          {faqs.items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div data-testid={`faq-item-${i}`} className="border-b border-stone-300">
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpenIdx(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="group flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono text-[10px] tracking-[0.25em] text-[#8F6B3C]/70">
                        0{i + 1}
                      </span>
                      <span
                        className={`font-serif text-lg transition-colors duration-300 md:text-xl ${
                          isOpen ? "text-[#8F6B3C]" : "text-stone-900 group-hover:text-[#8F6B3C]"
                        }`}
                      >
                        {item.q}
                      </span>
                    </span>
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-500 ${
                        isOpen
                          ? "rotate-45 border-[#C89D66] text-[#8F6B3C]"
                          : "border-stone-300 text-stone-500 group-hover:border-[#C89D66]/60 group-hover:text-[#8F6B3C]"
                      }`}
                    >
                      <Plus className="h-4 w-4" />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="max-w-2xl pb-7 pl-9 text-sm leading-relaxed text-stone-500 md:text-base">
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
